import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Filter, X, Download, RefreshCw } from "lucide-react";
import { exportXlsx } from "@/lib/xlsx";

const ACTION_LABEL = {
  CREATE: "Creación", UPDATE: "Actualización", DELETE: "Eliminación",
  DOCUMENT_UPLOAD: "Subida de documento", DOCUMENT_REPLACE: "Reemplazo de documento",
  DOCUMENT_DELETE: "Eliminación de documento",
  STATUS_CHANGE: "Cambio de estado", REASSIGN: "Reasignación",
  BULK_REMINDER: "Envío recordatorio", MANUAL_CONTACT: "Contacto manual",
  SCAN_CREDENCIAL: "Escaneo de credencial", BULK_DELETE: "Eliminación masiva",
};

const ENTITY_LABEL = {
  worker: "Trabajador", worker_requirement: "Requisito", requirement: "Requisito",
  mandante: "Mandante", mandante_doc: "Documento mandante",
  contrato: "Contrato", contrato_cargo: "Cargo contrato",
  jornada: "Jornada", workflow_stage: "Etapa flujo",
  worker_workflow: "Etapa trabajador", mandante_logo: "Logo mandante",
  document_type: "Tipo documento", campaign: "Campaña",
};

const PAGE_SIZE = 10;

export default function Auditoria() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mandantes, setMandantes] = useState([]);
  const [filters, setFilters] = useState({
    rut: "", mandante: "", contrato: "", action: "", entity: "", user_email: "",
    date_from: "", date_to: "",
  });
  const [applied, setApplied] = useState(filters);

  const load = async (f = applied, p = page) => {
    setLoading(true);
    try {
      const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), page: p, page_size: PAGE_SIZE };
      const { data } = await api.get("/audit-logs", { params });
      const items = data?.items || [];
      setRows(items);
      setTotal(Number(data?.total || 0));
      setPage(Number(data?.page || p));
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar la auditoría");
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  };

  // Snapshot sin filtros para poblar los dropdowns (una vez al montar).
  const loadAllForOptions = async () => {
    try {
      const { data } = await api.get("/audit-logs", { params: { limit: 2000 } });
      setAllRows(Array.isArray(data) ? data : (data?.items || []));
    } catch { setAllRows([]); }
  };

  useEffect(() => {
    api.get("/mandantes").then((r) => setMandantes(r.data || [])).catch(() => {});
    loadAllForOptions();
    load({}, 1);
    // eslint-disable-next-line
  }, []);

  const contratos = useMemo(() => {
    const m = mandantes.find((x) => x.name === filters.mandante);
    return m ? (m.contratos || []) : [];
  }, [mandantes, filters.mandante]);

  const uniqueUsers = useMemo(() => {
    return Array.from(new Set(allRows.map((r) => r.user_email).filter(Boolean))).sort();
  }, [allRows]);
  const uniqueActions = useMemo(() => {
    return Array.from(new Set(allRows.map((r) => r.action).filter(Boolean))).sort();
  }, [allRows]);

  const apply = () => { setApplied(filters); setPage(1); load(filters, 1); };
  const clear = () => {
    const empty = { rut: "", mandante: "", contrato: "", action: "", entity: "", user_email: "", date_from: "", date_to: "" };
    setFilters(empty); setApplied(empty); setPage(1); load(empty, 1);
  };
  const goTo = (p) => { setPage(p); load(applied, p); };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastIdx = Math.min(total, page * PAGE_SIZE);

  const describe = (r) => {
    const m = r.meta || {};
    const parts = [];
    if (m.worker_name) parts.push(m.worker_name);
    if (m.worker_rut) parts.push(`RUT ${m.worker_rut}`);
    if (m.document_name) parts.push(`Doc: ${m.document_name}`);
    if (m.filename) parts.push(m.filename);
    if (m.expiration_date) parts.push(`Vence ${m.expiration_date}`);
    if (m.name && !m.worker_name) parts.push(m.name);
    if (m.from && m.to) parts.push(`${JSON.stringify(m.from)} → ${JSON.stringify(m.to)}`);
    if (m.channel) parts.push(`Canal: ${m.channel}`);
    if (m.status) parts.push(`Estado: ${m.status}`);
    return parts.join(" · ") || "—";
  };

  const exportXlsxFile = () => {
    const data = rows.map((r) => {
      const m = r.meta || {};
      return {
        Fecha: r.created_at || "",
        Usuario: r.user_email || "",
        Acción: ACTION_LABEL[r.action] || r.action || "",
        Entidad: ENTITY_LABEL[r.entity] || r.entity || "",
        Mandante: m.mandante || "",
        Contrato: m.contrato || "",
        RUT: m.worker_rut || "",
        Detalle: describe(r),
      };
    });
    exportXlsx(data, `auditoria_${new Date().toISOString().slice(0, 10)}`, "Auditoría");
  };

  return (
    <div className="space-y-6" data-testid="auditoria-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={26} className="text-emerald-600" /> Auditoría
          </h1>
          <p className="text-slate-500 text-sm mt-1" data-testid="auditoria-count">
            Historial de acciones de la organización.{" "}
            {total > 0 ? (
              <>Mostrando <b>{firstIdx}–{lastIdx}</b> de <b>{total}</b> registros.</>
            ) : (
              <>0 registros.</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="auditoria-refresh" onClick={() => load(applied)} disabled={loading}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
          </button>
          <button data-testid="auditoria-export" onClick={exportXlsxFile} disabled={rows.length === 0}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-sm font-semibold text-white">
            <Download size={14} /> Exportar Excel
          </button>
        </div>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
          <Filter size={14} /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="RUT">
            <input data-testid="aud-filter-rut" value={filters.rut} onChange={(e) => setFilters((p) => ({ ...p, rut: e.target.value }))}
              placeholder="12.345.678-9" className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </Field>
          <Field label="Mandante">
            <select data-testid="aud-filter-mandante" value={filters.mandante} onChange={(e) => setFilters((p) => ({ ...p, mandante: e.target.value, contrato: "" }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Todos</option>
              {mandantes.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Contrato">
            <select data-testid="aud-filter-contrato" value={filters.contrato} onChange={(e) => setFilters((p) => ({ ...p, contrato: e.target.value }))}
              disabled={!filters.mandante}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white disabled:opacity-50">
              <option value="">Todos</option>
              {contratos.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Usuario">
            <select data-testid="aud-filter-user" value={filters.user_email} onChange={(e) => setFilters((p) => ({ ...p, user_email: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Todos</option>
              {uniqueUsers.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field label="Acción">
            <select data-testid="aud-filter-action" value={filters.action} onChange={(e) => setFilters((p) => ({ ...p, action: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Todas</option>
              {uniqueActions.map((a) => <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>)}
            </select>
          </Field>
          <Field label="Entidad">
            <select data-testid="aud-filter-entity" value={filters.entity} onChange={(e) => setFilters((p) => ({ ...p, entity: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Todas</option>
              {Object.entries(ENTITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Desde">
            <input data-testid="aud-filter-from" type="date" value={filters.date_from}
              onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </Field>
          <Field label="Hasta">
            <input data-testid="aud-filter-to" type="date" value={filters.date_to}
              onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </Field>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button data-testid="aud-apply" onClick={apply}
            className="h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
            Aplicar filtros
          </button>
          <button data-testid="aud-clear" onClick={clear}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700">
            <X size={14} /> Limpiar
          </button>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="auditoria-table">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="px-4 py-2 whitespace-nowrap">Fecha</th>
                <th className="px-4 py-2">Usuario</th>
                <th className="px-4 py-2">Acción</th>
                <th className="px-4 py-2">Entidad</th>
                <th className="px-4 py-2">Mandante</th>
                <th className="px-4 py-2">Contrato</th>
                <th className="px-4 py-2 whitespace-nowrap">RUT</th>
                <th className="px-4 py-2">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center text-slate-400 italic p-10">Cargando…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-slate-400 italic p-10">Sin registros para los filtros aplicados.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500">{fmtDateTime(r.created_at)}</td>
                  <td className="px-4 py-2 text-slate-700">{r.user_name || r.user_email}</td>
                  <td className="px-4 py-2"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{ACTION_LABEL[r.action] || r.action}</span></td>
                  <td className="px-4 py-2 text-xs text-slate-600">{ENTITY_LABEL[r.entity] || r.entity}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{(r.meta && r.meta.mandante) || "—"}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{(r.meta && r.meta.contrato) || "—"}</td>
                  <td className="px-4 py-2 text-xs font-mono text-slate-700 whitespace-nowrap">{(r.meta && r.meta.worker_rut) || "—"}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{describe(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50"
            data-testid="auditoria-pagination">
            <div className="text-xs text-slate-500">
              Página <b>{page}</b> de <b>{totalPages}</b> · {total} registros
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="aud-page-first" onClick={() => goTo(1)} disabled={page <= 1 || loading}
                className="h-8 px-2.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 text-xs font-medium text-slate-700">
                «
              </button>
              <button data-testid="aud-page-prev" onClick={() => goTo(Math.max(1, page - 1))} disabled={page <= 1 || loading}
                className="h-8 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 text-xs font-medium text-slate-700">
                Anterior
              </button>
              <span className="h-8 inline-flex items-center px-3 rounded-md bg-indigo-600 text-white text-xs font-semibold">
                {page}
              </span>
              <button data-testid="aud-page-next" onClick={() => goTo(Math.min(totalPages, page + 1))} disabled={page >= totalPages || loading}
                className="h-8 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 text-xs font-medium text-slate-700">
                Siguiente
              </button>
              <button data-testid="aud-page-last" onClick={() => goTo(totalPages)} disabled={page >= totalPages || loading}
                className="h-8 px-2.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 text-xs font-medium text-slate-700">
                »
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
      {children}
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yy} ${hh}:${mi}`;
  } catch { return iso; }
}
